import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import { LynxDropdown } from "components/LynxDropdown/LynxDropdown";
import { LynxInput } from "components/LynxInput/LynxInput";
import { observer } from "mobx-react";
import { ChangeEvent, useEffect, useState } from "react";
import { useStore } from "store/StoreConfigs";
import { FormProps } from "./FormProps";
import { formStyles } from "./FormStyles";
import { Customer } from "models/users/Customer";
import { MarvelModule } from "models/users/MarvelModule";
import { services } from "api/serviceConfig";
import { LynxButton } from "components/LynxButton/LynxButton";
import UserList from "./UserList";
import { UserItem } from "./UserListProps";

export default observer(function AddGroupForm(props: FormProps) {
    const classes = formStyles();
    const { identityStore, groupStore } = useStore();

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [name, setName] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>(groupStore.selectedCustomer);
    const [selectedModule, setSelectedModule] = useState<MarvelModule | undefined>(undefined);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

    useEffect(() => {
        groupStore.loadUsers(selectedCustomer?.id);
    }, [selectedCustomer]);

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        if (loading) {
            return;
        }

        setLoading(true);
        setErrors([]);

        try {
            let response;

            if (selectedCustomer) {
                response = await services.UserManagement.addCustomerGroup({
                    customerId: selectedCustomer.id,
                    name: name,
                    moduleId: selectedModule?.id,
                    users: selectedUsers,
                });
            } else {
                response = await services.UserManagement.addGlobalGroup({
                    name: name,
                    users: selectedUsers,
                });
            }

            if (response.status !== 200) {
                if (response.status === 400 && response.data.title === "One or more validation errors occurred.") {
                    setErrors(Object.entries(response.data.errors).flatMap((x) => x[1]) as string[]);
                } else if (response.status === 400 && response.data.errorMessage) {
                    setErrors([response.data.errorMessage]);
                } else {
                    console.log(response);
                    setErrors(["Unknown error occured. Check console output"]);
                }
            } else {
                props.successCallback({ groupName: name, customerName: selectedCustomer?.name });
            }
        } catch (error) {
            console.log(error);
            setErrors(["Unknown error occured. Check console output."]);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerChange = (e: ChangeEvent<{ value: unknown }>) => {
        const customer = identityStore.findCustomerByName(e.target.value as string);
        setSelectedCustomer(customer);
        setSelectedModule(undefined);
    };

    const handleModuleChange = (e: ChangeEvent<{ value: unknown }>) => {
        const module = selectedCustomer?.modules.find((x) => x.name === e.target.value);
        setSelectedModule(module);
    };

    const handleSelectionChange = (newSelection: UserItem[]) => {
        console.log(newSelection);
        setSelectedUsers(newSelection.map((x) => x.id));
    };

    return (
        <Box padding={2}>
            <Typography variant="h3" gutterBottom>
                Add User Group
            </Typography>

            <form className={classes.form} onSubmit={handleSubmit}>
                <LynxInput
                    size="large"
                    name="name"
                    value={name}
                    label="Name"
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. TOR QA"
                    formControlClassName={classes.input}
                />
                {identityStore.isGlobalUser && (
                    <LynxDropdown
                        isCustomizable={false}
                        options={["-- global --", ...identityStore.allCustomers.map((x) => x.name)]}
                        optionState={[selectedCustomer ? selectedCustomer.name : "-- global --"]}
                        label="Customer"
                        title="Select customer"
                        multiple={false}
                        stateSetMethod={handleCustomerChange}
                        customClass={clsx(classes.dropdown, classes.input)}
                    />
                )}
                {selectedCustomer && (
                    <LynxDropdown
                        isCustomizable={false}
                        options={["-- no module --", ...selectedCustomer.modules.map((x) => x.name)]}
                        optionState={[selectedModule ? selectedModule.name : "-- no module --"]}
                        label="Module"
                        title="Select module"
                        multiple={false}
                        stateSetMethod={handleModuleChange}
                        customClass={clsx(classes.dropdown, classes.input)}
                    />
                )}
                {groupStore.users.length > 0 && (
                    <UserList users={groupStore.users} onSelectionChange={handleSelectionChange} />
                )}
                <LynxButton
                    className={classes.submitButton}
                    size="large"
                    responsive={false}
                    type="submit"
                    loading={loading}
                    disabled={loading}
                >
                    Submit
                </LynxButton>
                {errors.length > 0 &&
                    errors.map((x) => (
                        <Typography key={x} variant="body1" className={classes.error}>
                            {x}
                        </Typography>
                    ))}
            </form>
        </Box>
    );
});
